import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, Trash2, FileDown } from 'lucide-react';
import { apiPost, apiPut } from '../../lib/api';
import { inr2, fmtDMY } from '../../lib/format';
import VendorPicker from '../pickers/VendorPicker';
import { ChargesTable, cleanCharges, chargesFromPo } from './ChargesTable';
import { downloadPdf } from '../../lib/downloadPdf';

const lbl = 'text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1';
const inp = 'w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm';

/** #9 — edit a pending PO (mode 'edit') or revise an approved one (mode 'revise'). */
export function POEditModal({ po, mode, onClose, onSaved }) {
  const [f, setF] = useState({
    vendor_id: po.vendor_id, vendor: po.vendor_name, delivery_date: po.delivery_date || '',
    payment_terms: po.payment_terms || '', ship_to_address: po.ship_to_address || po.ship_to || '',
    items: (po.items || []).map(it => ({ ...it, qty: it.qty ?? it.quantity })), charges: chargesFromPo(po), reason: '',
  });
  const [saving, setSaving] = useState(false);
  const revise = mode === 'revise';
  const projects = [...new Set(f.items.map(i => i.project_id).filter(Boolean))].map(id => ({ id, name: f.items.find(i => i.project_id === id)?.project_name }));
  const upd = (i, k, v) => setF({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) });

  const save = async () => {
    if (revise && !f.reason.trim()) { toast.error('A reason is required to revise a PO'); return; }
    setSaving(true);
    const body = {
      delivery_date: f.delivery_date || undefined, payment_terms: f.payment_terms, ship_to_address: f.ship_to_address,
      items: f.items.map(it => ({ ...it, qty: Number(it.qty), rate: Number(it.rate), gst_rate: Number(it.gst_rate) })),
      charges: cleanCharges(f.charges), reason: f.reason || undefined,
      ...(revise ? {} : { vendor_id: f.vendor_id }),
    };
    try {
      const out = revise ? await apiPost(`/po/${po.id}/revise`, body) : await apiPut(`/po/${po.id}`, body);
      toast.success(revise ? `Revision ${out.revision} created — sent for approval` : 'PO updated');
      onSaved?.(out);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" data-testid="po-edit-modal">
      <div className="bg-white rounded-sm w-full max-w-4xl max-h-[92vh] overflow-y-auto p-5 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-display font-bold text-lg text-[#3b4872]">{revise ? `Revise PO ${po.po_no} → Rev ${(po.revision || 0) + 1}` : `Edit PO ${po.po_no}`}</h3>
          <button onClick={onClose} data-testid="po-edit-close"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><span className={lbl}>Vendor</span>
            {revise ? <div className="text-sm font-semibold py-1.5" data-testid="po-edit-vendor-locked">{po.vendor_name} <span className="text-xs text-slate-400">(locked on revision)</span></div>
              : <VendorPicker value={f.vendor_id} displayName={f.vendor} onSelect={v => setF({ ...f, vendor_id: v?.id || '', vendor: v?.name || '' })} testid="po-edit-vendor" />}
          </div>
          <div><span className={lbl}>Delivery date</span><input type="date" value={f.delivery_date?.slice(0, 10) || ''} onChange={e => setF({ ...f, delivery_date: e.target.value })} className={inp} data-testid="po-edit-delivery-date" /></div>
          <div><span className={lbl}>Payment terms</span><input value={f.payment_terms} onChange={e => setF({ ...f, payment_terms: e.target.value })} className={inp} data-testid="po-edit-payment-terms" /></div>
          <div><span className={lbl}>Delivery address</span><input value={f.ship_to_address} onChange={e => setF({ ...f, ship_to_address: e.target.value })} className={inp} data-testid="po-edit-ship-to" /></div>
        </div>
        <table className="w-full text-xs border border-slate-200">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="px-2 py-1 text-left">Item</th><th className="px-2 py-1 text-left">MPR / Project</th>
            <th className="px-2 py-1 text-right">Qty</th><th className="px-2 py-1 text-right">Rate</th><th className="px-2 py-1 text-right">GST %</th><th />
          </tr></thead>
          <tbody>{f.items.map((it, i) => (
            <tr key={i} className="border-t border-slate-100" data-testid={`po-edit-line-${i}`}>
              <td className="px-2 py-1">{it.material_name || it.description}</td>
              <td className="px-2 py-1 text-slate-500">{it.mpr_no || '—'} · {it.project_name || ''}</td>
              <td className="px-2 py-1 text-right"><input type="number" min="0" step="any" value={it.qty} onChange={e => upd(i, 'qty', e.target.value)} className="w-20 px-1.5 py-0.5 border border-slate-300 rounded-sm text-right" data-testid={`po-edit-qty-${i}`} /></td>
              <td className="px-2 py-1 text-right"><input type="number" min="0" step="any" value={it.rate} onChange={e => upd(i, 'rate', e.target.value)} className="w-24 px-1.5 py-0.5 border border-slate-300 rounded-sm text-right" data-testid={`po-edit-rate-${i}`} /></td>
              <td className="px-2 py-1 text-right"><input type="number" min="0" max="28" step="0.01" value={it.gst_rate} onChange={e => upd(i, 'gst_rate', e.target.value)} className="w-16 px-1.5 py-0.5 border border-slate-300 rounded-sm text-right" data-testid={`po-edit-gst-${i}`} /></td>
              <td className="px-2 py-1">{!revise && f.items.length > 1 && <button onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })} className="text-rose-500" data-testid={`po-edit-remove-${i}`}><Trash2 size={12} /></button>}</td>
            </tr>))}
          </tbody>
        </table>
        <ChargesTable charges={f.charges} onChange={c => setF({ ...f, charges: c })} items={f.items} projects={projects} intra={po.gst_split_type !== 'inter'} testid="po-edit-charges" />
        <div><span className={lbl}>Reason {revise ? '*' : '(optional)'}</span>
          <input value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} className={inp} placeholder="Why is this PO being changed?" data-testid="po-edit-reason" /></div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 bg-[#3b4872] text-white text-sm rounded-sm font-semibold disabled:opacity-50" data-testid="po-edit-save">
            {saving ? 'Saving…' : revise ? 'Create revision' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

const fmtChange = (c) => {
  const what = c.line ? `Line ${c.line}` : c.charge ? `Charge ${c.charge}` : c.field;
  return `${what}${c.field && (c.line || c.charge) ? ` · ${c.field}` : ''}: ${c.old ?? '—'} → ${c.new ?? '—'}`;
};

/** #9 — History tab: every edit / revision with who, when, reason and the differences. */
export function POHistory({ po }) {
  const hist = [...(po.revision_history || [])].reverse();
  if (!hist.length) return <div className="text-xs text-slate-400 p-3" data-testid="po-history-empty">No edits or revisions yet.</div>;
  const snaps = new Set((po.revisions || []).map(r => Number(r.revision || 0)));
  return (
    <div className="space-y-3" data-testid="po-history">
      {hist.map((h, i) => (
        <div key={i} className="border border-slate-200 rounded-sm p-3 text-xs" data-testid={`po-history-entry-${i}`}>
          <div className="flex justify-between">
            <b className="text-[#3b4872]">{h.type === 'revision' ? `Rev ${h.revision}` : h.type === 'revision_rejected' ? `Revision rejected (Rev ${h.revision})` : `Edit (Rev ${h.revision})`}</b>
            <span className="text-slate-500">{h.edited_by} · {fmtDMY(h.edited_at)}</span>
          </div>
          {h.reason && <div className="mt-1 text-slate-600">Reason: {h.reason}</div>}
          <ul className="mt-1 list-disc ml-4 text-slate-700">{(h.changes || []).map((c, j) => <li key={j}>{fmtChange(c)}</li>)}</ul>
          {h.type === 'revision' && snaps.has(h.revision - 1) && (
            <button onClick={() => downloadPdf(`/po/${po.id}/revisions/${h.revision - 1}/pdf`, `PO_${po.po_no}_Rev${h.revision - 1}.pdf`.replace(/\//g, '-'))}
              className="mt-2 flex items-center gap-1 text-[#3b4872] font-semibold" data-testid={`po-history-pdf-${h.revision - 1}`}>
              <FileDown size={12} /> PDF of Rev {h.revision - 1} {h.revision - 1 === 0 ? '(original)' : ''}
            </button>)}
        </div>
      ))}
    </div>
  );
}

export const inrTotal = inr2;
